import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { Button, Modal } from 'components';
import { reduxForm, getFormSyncErrors, Field } from 'redux-form';
import { IStore } from 'redux/IStore';
import { IBookAppointmentObject } from 'models/bookappointment';
import { connect, Dispatch } from 'react-redux';
import { closeModal } from 'redux/modules/modal';
import { currentOffice } from 'containers/Shop/Helpers';
import renderIcon from 'helpers/renderIcon';
import { PortalCheckboxField } from 'components/FormControls';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { RouteComponentProps } from 'react-router';
import { formatCurrency, formatDuration } from 'helpers/common';
import { IOfficeMember } from 'models/shop/members';

interface IParams {
	shopId: string;
	officeId: string;
}

interface IOwnProps extends RouteComponentProps<IParams, {}> {
	variantParentId: string;
	showHeaderCloseButton?: boolean;
	setStaffForService: (variantParentId: string, resourceId: string | null) => boolean;
}

type IProps = IOwnProps &
	InjectedFormProps &
	ReturnType<typeof stateToProps> &
	ReturnType<typeof dispatchToProps> &
	ITranslateProps;

export const FIRST_AVAILABLE = 'firstAvailable';

const formKey = 'memberChosen';

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class SelectStaffModal extends React.Component<IProps, any> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
	}

	public componentDidMount(): void {
		const { currentFormValues, variantParentId, change } = this.props;
		const selectedForService = currentFormValues.selectStaff?.[variantParentId];
		if (selectedForService) {
			for (const serviceId in selectedForService) {
				change(formKey, selectedForService[serviceId]);
				break;
			}
		} else {
			change(formKey, null);
		}
	}

	public onSubmit() {
		this.selectStaffMember();
	}

	private selectStaffMember() {
		const { currentFormValues, closeModal, variantParentId } = this.props;
		const memberChosen = currentFormValues.memberChosen;
		if (this.props.setStaffForService(variantParentId, memberChosen === FIRST_AVAILABLE ? null : memberChosen)) {
			closeModal();
		}
	}

	private get services() {
		const { variantParentId, shopBookingTemplates, params } = this.props;
		return (shopBookingTemplates.services || []).filter(
			(s) => variantParentId == s.variantParentId && s.officeId === params.officeId,
		);
	}

	private renderResource(id: string, image: string, name: string, price: string, duration: string, jobTitle?: string) {
		const { currentFormValues } = this.props;
		const isActive =
			currentFormValues?.memberChosen === id || (id === FIRST_AVAILABLE && !currentFormValues?.memberChosen);
		const picture = image ? <img src={image} /> : renderIcon(id === FIRST_AVAILABLE ? 'smile_happy' : 'pfp_default');
		return (
			<label
				key={id}
				className={'card userCardPicture ' + (isActive ? 'active' : 'gray') + (id === FIRST_AVAILABLE ? ' first' : '')}
			>
				<div className="checkbox">
					<Field name="memberChosen" component={PortalCheckboxField} type="radio" value={id} className="members" />
				</div>
				<div className="staffInfo">
					<p className="p18 name">{name}</p>
					<p>{jobTitle ? jobTitle : <>&nbsp;</>}</p>
					<p>
						{price} • {duration}
					</p>
				</div>
				<div className="photo-container">{picture}</div>
			</label>
		);
	}

	private renderStaffMembers(): JSX.Element[][] | null {
		const { t, shopCard } = this.props;
		const currencyKey = shopCard.currencyKey;
		const services = this.services;
		if (!services.length) {
			return null;
		}
		const renderedIds: IOfficeMember['id'][] = [];
		const allRendered = services.map((service) =>
			service.resources.map((resource: IOfficeMember) => {
				const staffMember = this.props.staff.find((member: IOfficeMember) => member.id == resource.id) as IOfficeMember;
				if (renderedIds.includes(resource.id)) {
					return null;
				}
				renderedIds.push(resource.id);
				return this.renderResource(
					resource.id,
					staffMember.picture,
					staffMember.name,
					formatCurrency(currencyKey, service.price),
					formatDuration(service.duration),
					staffMember?.jobTitle,
				);
			}),
		);
		if (renderedIds.length) {
			const cheapest = services.reduce((acc, val) => (val.price < acc ? val.price : acc), Infinity);
			const mostExpensive = services.reduce((acc, val) => (val.price > acc ? val.price : acc), 0);
			const shortest = services.reduce((acc, val) => (val.duration < acc ? val.duration : acc), Infinity);
			const longest = services.reduce((acc, val) => (val.duration > acc ? val.duration : acc), 0);
			allRendered.unshift([
				this.renderResource(
					FIRST_AVAILABLE,
					undefined,
					t('BookAppointment_ServicesStaff_FirstAvailable'),
					cheapest === mostExpensive
						? formatCurrency(currencyKey, cheapest)
						: formatCurrency(currencyKey, cheapest) + ' - ' + formatCurrency(currencyKey, mostExpensive),
					shortest === longest ? formatDuration(shortest) : formatDuration(shortest) + ' - ' + formatDuration(longest),
				),
			]);
		}
		return allRendered;
	}

	public render() {
		const { t, closeModal, handleSubmit, showHeaderCloseButton } = this.props;
		const services = this.services;
		const name = services.length ? services[0].name : '';
		return (
			<Modal modalClass={'selectStaffModal'} showHeaderCloseButton={showHeaderCloseButton} closeModal={closeModal}>
				<h1>{t('BookAppointment_ServicesStaff_SelectStaffModal_Title')}</h1>
				<p>
					<strong>{t('BookAppointment_ServicesStaff_SelectStaffModal_Service')}</strong> {name}
				</p>
				<div className="staffMembers">
					<form onSubmit={handleSubmit(this.onSubmit)}>
						<div className="staffMembersRow">{this.renderStaffMembers()}</div>
						<div className="buttonRow">
							<Button
								size="small"
								type="button"
								onClick={() => {
									this.selectStaffMember();
								}}
							>
								{t('BSPortal_Common_Done')}
							</Button>
						</div>
					</form>
				</div>
			</Modal>
		);
	}
}

let SelectStaffModalForm = reduxForm({
	form: c.FORM_NAME_BOOKAPPOINTMENT,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: false,
})(SelectStaffModal);

const stateToProps = (state: IStore, ownProps: IOwnProps) => {
	let currentFormValues: IBookAppointmentObject = null;
	if (state.form[c.FORM_NAME_BOOKAPPOINTMENT] && state.form[c.FORM_NAME_BOOKAPPOINTMENT].values) {
		currentFormValues = state.form[c.FORM_NAME_BOOKAPPOINTMENT].values;
	}

	return {
		currentFormValues,
		shopBookingTemplates: state.shopBookingTemplates,
		shopCard: state.shopcard.shopcard,
		formErrors: getFormSyncErrors(c.FORM_NAME_BOOKAPPOINTMENT)(state),
		staff:
			currentOffice(state.shopcard.shopcard.offices, ownProps.params.officeId, ownProps.params.shopId)?.staff || [],
	};
};

const dispatchToProps = (dispatch: Dispatch<IStore>) => ({
	closeModal: () => dispatch(closeModal()),
});

SelectStaffModalForm = connect(stateToProps, dispatchToProps)(SelectStaffModalForm);

export { SelectStaffModalForm };
